import { supabase } from "@/lib/supabase";
import { aiService } from "./aiService";

export interface PersonalizedSuggestion {
  icon: string;
  prompt: string;
  category: string;
}

export interface ChatGreeting {
  headline: string;
  subtext: string;
}

interface ThreadContext {
  id: string;
  title: string;
  topic: string;
  keyThemes: string[];
  lastActivity: Date;
}

const THEME_KEYWORDS = [
  "productivity", "success", "growth", "learning", "leadership", "innovation",
  "creativity", "motivation", "resilience", "mindset", "focus", "goals",
  "business", "career", "health", "wellness", "relationships", "money",
  "investing", "technology", "science", "philosophy", "spirituality",
  "happiness", "purpose", "meaning", "change", "improvement", "strategy",
  "problem", "solution", "challenge", "opportunity", "skill", "knowledge",
  "research", "analysis", "decision", "planning", "execution", "progress",
  "AI", "machine learning", "data", "coding", "programming", "design",
  "marketing", "sales", "finance", "psychology", "fitness", "nutrition",
  "writing", "communication", "networking", "automation", "startup",
  "entrepreneurship", "freelance", "remote work", "productivity tools"
];

const DEFAULT_SUGGESTIONS: PersonalizedSuggestion[] = [
  { icon: "Sparkles", prompt: "Give me 3 ideas for learning something new today", category: "learning" },
  { icon: "FileText", prompt: "Help me summarize and analyze a topic", category: "analysis" },
  { icon: "Target", prompt: "What questions should I be asking about my goals?", category: "planning" },
  { icon: "Search", prompt: "Help me research and compare options", category: "research" },
];

const WITTY_GREETINGS = {
  morning: [
    { headline: "Rise and think, {name}", subtext: "Your brain is freshest now. What challenge shall we tackle?" },
    { headline: "Coffee for thoughts", subtext: "I'm caffeinated with knowledge, {name}. Let's brew some ideas." },
    { headline: "Good morning, {name}", subtext: "Ready to turn those shower thoughts into action plans?" },
    { headline: "Dawn of brilliance", subtext: "Early bird gets the insights. What's on your mind, {name}?" },
    { headline: "Morning, {name}", subtext: "The best thinking happens before the world gets noisy." },
  ],
  afternoon: [
    { headline: "Afternoon brain boost, {name}", subtext: "Beat that post-lunch fog with a productive conversation." },
    { headline: "Hey {name}", subtext: "Let's keep that productive energy rolling this afternoon." },
    { headline: "Peak curiosity hours", subtext: "Your brain's warmed up, {name}. What shall we explore?" },
    { headline: "{name}, perfect timing", subtext: "Afternoon is great for tackling that thing you've been putting off." },
  ],
  evening: [
    { headline: "Evening reflections, {name}", subtext: "Great minds reflect. What did today teach you?" },
    { headline: "Night owl mode", subtext: "Some of the best ideas come when the world quiets down, {name}." },
    { headline: "Moonlight musings", subtext: "Ready for some deep thinking, {name}? I'm all ears." },
    { headline: "{name}'s evening wisdom", subtext: "Let's turn today's experiences into tomorrow's advantages." },
  ],
  personalized: [
    { headline: "Welcome back, {name}", subtext: "I noticed you've been diving into {topic}. Ready to go deeper?" },
    { headline: "The sequel begins, {name}", subtext: "Last time we explored {topic}. Where shall the journey continue?" },
    { headline: "{name}'s knowledge awaits", subtext: "Pick up where you left off, or start a new adventure?" },
    { headline: "Continuing your quest, {name}", subtext: "You've been building expertise in {topic}. Let's expand it." },
    { headline: "Hey {name}", subtext: "Your {topic} journey is looking impressive. What's next?" },
  ],
  witty: [
    { headline: "I was just thinking about you, {name}", subtext: "Specifically about how we could make {topic} even more interesting." },
    { headline: "Plot twist incoming, {name}", subtext: "What if we looked at {topic} from a completely different angle?" },
    { headline: "{name}, curiosity called", subtext: "It left a message about {topic}. Should we investigate?" },
    { headline: "The rabbit hole beckons", subtext: "Your interest in {topic} has opened new possibilities, {name}." },
    { headline: "{name}'s next adventure", subtext: "I've been thinking about what you might want to explore next." },
  ],
  newUser: [
    { headline: "Welcome, {name}", subtext: "I'm excited to help you explore and learn. What's on your mind?" },
    { headline: "Hey {name}, let's begin", subtext: "Every great journey starts with a single question. What's yours?" },
    { headline: "Nice to meet you, {name}", subtext: "I'm here to help you think, learn, and create. Where shall we start?" },
    { headline: "{name}, you're in good hands", subtext: "Tell me what you're curious about and let's dive in." },
  ]
};

class ChatSuggestionsService {
  private extractKeyThemes(content: string): string[] {
    const lowerContent = content.toLowerCase();
    return THEME_KEYWORDS.filter(keyword => 
      lowerContent.includes(keyword.toLowerCase())
    ).slice(0, 5);
  }

  private async getRecentThreads(userId: string, limit: number = 15): Promise<ThreadContext[]> {
    try {
      // Exclude messages to avoid huge payloads (net::ERR_FAILED 200); use topic for keyThemes
      const { data: threads, error } = await supabase
        .from("chat-threads")
        .select("id, title, topic, updatedAt")
        .eq("userId", userId)
        .order("updatedAt", { ascending: false })
        .limit(limit);

      if (error || !threads) {
        console.error("Error fetching threads for suggestions:", error);
        return [];
      }

      return threads.map((thread: any) => {
        const topic = thread.topic || "";
        return {
          id: thread.id,
          title: thread.title || "Untitled",
          topic,
          keyThemes: this.extractKeyThemes(topic),
          lastActivity: new Date(thread.updatedAt || Date.now()),
        };
      });
    } catch (error) {
      console.error("Error in getRecentThreads:", error);
      return [];
    }
  }

  private getTimeOfDay(): "morning" | "afternoon" | "evening" {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return "morning";
    if (hour >= 12 && hour < 18) return "afternoon";
    return "evening";
  }

  private pickRandom<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  private getMostFrequentTopics(threads: ThreadContext[]): string[] {
    const topicCount = new Map<string, number>();
    
    threads.forEach(thread => {
      thread.keyThemes.forEach(theme => {
        topicCount.set(theme, (topicCount.get(theme) || 0) + 1);
      });
      if (thread.topic) {
        topicCount.set(thread.topic, (topicCount.get(thread.topic) || 0) + 2);
      }
    });

    return Array.from(topicCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([topic]) => topic);
  }

  async getPersonalizedGreeting(userId: string, userName?: string): Promise<ChatGreeting> {
    const threads = await this.getRecentThreads(userId, 10);
    const timeOfDay = this.getTimeOfDay();
    
    // Extract first name from full name, or use fallback
    const firstName = userName 
      ? userName.split(" ")[0] 
      : null;
    
    const replaceName = (text: string): string => {
      if (firstName) {
        return text.replace(/\{name\}/g, firstName);
      }
      // Remove name placeholders gracefully if no name available
      return text
        .replace(/,\s*\{name\}/g, "")
        .replace(/\{name\},\s*/g, "")
        .replace(/\{name\}'s\s*/g, "Your ")
        .replace(/\{name\}/g, "friend");
    };
    
    if (threads.length === 0) {
      // New user - use special newUser greetings if we have a name
      if (firstName) {
        const template = this.pickRandom(WITTY_GREETINGS.newUser);
        return {
          headline: replaceName(template.headline),
          subtext: replaceName(template.subtext),
        };
      }
      const template = this.pickRandom(WITTY_GREETINGS[timeOfDay]);
      return {
        headline: replaceName(template.headline),
        subtext: replaceName(template.subtext),
      };
    }

    const topTopics = this.getMostFrequentTopics(threads);
    const primaryTopic = topTopics[0] || "interesting topics";

    const usePersonalized = Math.random() > 0.3;
    
    if (usePersonalized && topTopics.length > 0) {
      const greetingPool = [...WITTY_GREETINGS.personalized, ...WITTY_GREETINGS.witty];
      const template = this.pickRandom(greetingPool);
      
      return {
        headline: replaceName(template.headline.replace(/\{topic\}/g, primaryTopic)),
        subtext: replaceName(template.subtext.replace(/\{topic\}/g, primaryTopic)),
      };
    }

    const template = this.pickRandom(WITTY_GREETINGS[timeOfDay]);
    return {
      headline: replaceName(template.headline),
      subtext: replaceName(template.subtext),
    };
  }

  async getPersonalizedSuggestions(userId: string): Promise<PersonalizedSuggestion[]> {
    const threads = await this.getRecentThreads(userId, 15);
    
    if (threads.length < 3) {
      return DEFAULT_SUGGESTIONS;
    }

    const topTopics = this.getMostFrequentTopics(threads);
    const recentTitles = threads.slice(0, 5).map(t => t.title);

    try {
      const suggestions = await this.generateAISuggestions(topTopics, recentTitles);
      if (suggestions && suggestions.length >= 4) {
        return suggestions;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (!message.includes("API Error: 401")) {
        console.error("Failed to generate AI suggestions:", error);
      }
    }

    return this.generateFallbackSuggestions(topTopics, recentTitles);
  }

  private async generateAISuggestions(
    topics: string[], 
    recentTitles: string[]
  ): Promise<PersonalizedSuggestion[]> {
    const prompt = `Based on a user's recent interests and conversations, generate 4 personalized chat prompt suggestions.

User's key interests: ${topics.join(", ")}
Recent conversation titles: ${recentTitles.join(", ")}

Generate 4 diverse suggestions that:
1. Build on their existing interests but offer fresh angles
2. Connect different topics they've explored
3. Encourage deeper exploration
4. Include something slightly unexpected but relevant

Return as JSON array with format:
[
  {"icon": "Sparkles|FileText|Target|Search|Brain|Lightbulb|Compass|Telescope", "prompt": "suggestion text", "category": "learning|analysis|planning|research|creativity|exploration"}
]

Keep prompts conversational and under 60 characters. Be creative and slightly playful.`;

    const response = await aiService.complete(prompt, { 
      model: "openai/gpt-4o-mini",
      maxTokens: 500,
      temperature: 0.8,
      suppressErrors: true,
      app: "other",
    });

    const jsonMatch = response.match(/\[[\s\S]*?\]/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      if (Array.isArray(parsed) && parsed.length >= 4) {
        return parsed.slice(0, 4).map(s => ({
          icon: s.icon || "Sparkles",
          prompt: s.prompt,
          category: s.category || "exploration"
        }));
      }
    }

    throw new Error("Failed to parse AI suggestions");
  }

  private generateFallbackSuggestions(
    topics: string[],
    recentTitles: string[]
  ): PersonalizedSuggestion[] {
    const suggestions: PersonalizedSuggestion[] = [];
    
    if (topics.length > 0) {
      suggestions.push({
        icon: "Brain",
        prompt: `What's the next level in my ${topics[0]} journey?`,
        category: "growth"
      });
    }

    if (topics.length > 1) {
      suggestions.push({
        icon: "Lightbulb",
        prompt: `How do ${topics[0]} and ${topics[1]} connect?`,
        category: "synthesis"
      });
    }

    if (recentTitles.length > 0) {
      const shortTitle = recentTitles[0].substring(0, 30);
      suggestions.push({
        icon: "Compass",
        prompt: `Take "${shortTitle}" in a new direction`,
        category: "exploration"
      });
    }

    suggestions.push({
      icon: "Telescope",
      prompt: "Show me something I haven't thought to explore yet",
      category: "discovery"
    });

    while (suggestions.length < 4) {
      suggestions.push(DEFAULT_SUGGESTIONS[suggestions.length % DEFAULT_SUGGESTIONS.length]);
    }

    return suggestions.slice(0, 4);
  }

  getDefaultSuggestions(): PersonalizedSuggestion[] {
    return DEFAULT_SUGGESTIONS;
  }

  getDefaultGreeting(userName?: string): ChatGreeting {
    const timeOfDay = this.getTimeOfDay();
    const template = this.pickRandom(WITTY_GREETINGS[timeOfDay]);
    
    // Replace name placeholders
    const firstName = userName ? userName.split(" ")[0] : null;
    const replaceName = (text: string): string => {
      if (firstName) {
        return text.replace(/\{name\}/g, firstName);
      }
      return text
        .replace(/,\s*\{name\}/g, "")
        .replace(/\{name\},\s*/g, "")
        .replace(/\{name\}'s\s*/g, "Your ")
        .replace(/\{name\}/g, "");
    };
    
    return {
      headline: replaceName(template.headline),
      subtext: replaceName(template.subtext),
    };
  }
}

export const chatSuggestionsService = new ChatSuggestionsService();
